type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];

/**
 * Performs a deep merge of delta into obj, returning a new object with combined values.
 * Null values in delta indicate properties that should be omitted in the result (unless preserveNull is true)
 * Preserves reference equality of objects and arrays when no changes are needed
 * @param {JsonObject} obj - The object to merge into
 * @param {JsonObject} delta - The delta object containing changes to apply
 * @param {boolean} options.preserveNull - If true, null values in delta will be preserved instead of omitted
 * @returns {JsonObject} - A new object if changes were made, or the original object if no changes. A null delta will return the original object if that object is empty, otherwise it will return a new empty object.
 */
export function deepMerge(
  obj: JsonObject,
  delta: JsonObject | null,
  options: { preserveNull: boolean } = { preserveNull: false }
): JsonObject {
  if (delta === null) {
    if (Object.keys(obj).length === 0) {
      return obj;
    } else {
      return {};
    }
  }

  // Return target if source is not an object
  if (!isObject(delta)) {
    return obj;
  }

  // Return target if source is empty (no changes needed)
  if (Object.keys(delta).length === 0) {
    return obj;
  }

  let hasChanges = false;
  const result: JsonObject = {};

  // Copy all properties from target first
  for (const key in obj) {
    if (
      Object.prototype.hasOwnProperty.call(obj, key) &&
      !Object.prototype.hasOwnProperty.call(delta, key)
    ) {
      result[key] = obj[key];
    }
  }

  // Process source properties
  for (const key in delta) {
    if (Object.prototype.hasOwnProperty.call(delta, key)) {
      const deltaValue = delta[key];
      const originalValue = obj[key];

      // Skip null values (they should be omitted)
      if (deltaValue === null && !options.preserveNull) {
        hasChanges = true;
        continue;
      } else if (deltaValue === null && options.preserveNull) {
        hasChanges = true;
        result[key] = null;
        continue;
      }
      // For arrays and primitives, just check reference/value equality
      if (!isObject(deltaValue)) {
        if (deltaValue !== originalValue) {
          hasChanges = true;
          result[key] = deltaValue;
        } else {
          result[key] = originalValue;
        }
      }
      // Handle nested objects
      else if (isObject(deltaValue)) {
        if (isObject(originalValue)) {
          const mergedValue = deepMerge(originalValue, deltaValue, options);
          if (mergedValue !== originalValue) {
            hasChanges = true;
          }
          result[key] = mergedValue;
        } else {
          hasChanges = true;
          result[key] = deepMerge({}, deltaValue, options);
        }
      }
    }
  }

  return hasChanges ? result : obj;
}

/**
 * Helper function to check if a value is an object
 * @param {unknown} value - The value to check
 * @returns {boolean} - True if the value is an object and not null or an array
 */
function isObject(value: unknown): value is JsonObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
